import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { Globe, ArrowLeft } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import VrestoEdoLogo from "../assets/VrestoEdo_Logo.png";
import { documentCategories } from "../data/documentCategories";

const Header = () => {
  const { language, setLanguage } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [dynamicTitle, setDynamicTitle] = useState("");
  const [isHome, setIsHome] = useState(location.pathname === "/");

  useEffect(() => {
    const path = location.pathname;
    setIsHome(path === "/");

    // === Handle category and subcategory ===
    const pathParts = path.split("/").filter(Boolean);
    if (pathParts.length === 2 && pathParts[0] === "category") {
      const cat = documentCategories.find((c) => c.id === parseInt(pathParts[1]));
      if (cat) setDynamicTitle(language === "el" ? cat.title : cat.titleEn);
    } else if (pathParts.length === 2 && pathParts[0] === "subcategory") {
      let parent = null;
      let sub = null;
      for (const c of documentCategories) {
        const s = c.subcategories?.find((sc) => sc.id === pathParts[1]);
        if (s) {
          parent = c;
          sub = s;
          break;
        }
      }
      if (sub && parent)
        setDynamicTitle(
          language === "el"
            ? `${parent.title} / ${sub.name}`
            : `${parent.titleEn} / ${sub.nameEn}`
        );
    } else {
      setDynamicTitle("");
    }
  }, [location, language]);

  const handleBack = () => navigate(-1);
  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate("/");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 300);
  };

  return (
    <header
      className="
        fixed top-0 left-0 w-full z-50
        backdrop-blur-lg bg-white/5 border-b border-white/10 text-white
        py-4 px-6 sm:px-10
        flex flex-col md:grid md:grid-cols-[1fr_auto_1fr] md:items-center
        gap-3 md:gap-6
        transition-all duration-500 ease-in-out
      "
    >
      {/* === Left Section (Logo + Mobile Buttons) === */}
      <div
        className="
          flex items-center justify-between md:justify-start gap-3 w-full md:w-auto
          transition-all duration-500 ease-in-out
        "
      >
        <a
          href="/"
          onClick={handleLogoClick}
          className="group transition-all duration-500 ease-in-out cursor-pointer"
        >
          <div
            className="
              flex items-center justify-center rounded-2xl shadow-lg
              transition-all duration-500 ease-in-out transform group-hover:scale-110
              group-hover:shadow-[0_0_40px_rgba(59,130,246,0.9)]
              w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16
            "
            style={{ backgroundColor: "#1D2951" }}
          >
            <img
              src={VrestoEdoLogo}
              alt="VrestoEdo.gr"
              className="object-contain h-full w-full transition-all duration-500 ease-in-out"
            />
          </div>
        </a>

        <div className="flex items-center gap-3 md:hidden transition-all duration-500 ease-in-out">
          {!isHome && (
            <button
              onClick={handleBack}
              className="
                flex items-center gap-1 text-sm text-blue-100 
                px-2 py-1 rounded-lg border border-white/20 
                bg-white/10 backdrop-blur-md 
                hover:bg-white/20 transition-all duration-500 ease-in-out
              "
            >
              <ArrowLeft size={14} />
            </button>
          )}

          <button
            onClick={() => setLanguage(language === "el" ? "en" : "el")}
            className="flex items-center gap-1 hover:text-blue-300 transition-all duration-500 ease-in-out text-sm"
          >
            <Globe size={14} />
            {language === "el" ? "EN" : "ΕΛ"}
          </button>
        </div>
      </div>

      {/* === Center Section (Dynamic Title) === */}
      <div
        className="
          flex flex-col items-center justify-center text-center w-full
          transition-all duration-500 ease-in-out
        "
      >
        <h1
          className="
            text-2xl font-bold tracking-wide mb-1 leading-tight
            [font-size:clamp(1.25rem,2vw,1.75rem)]
            transition-all duration-500 ease-in-out
          "
        >
          {dynamicTitle
            ? dynamicTitle
            : language === "el"
            ? "Βρες Εύκολα Online Έγγραφα και Υπηρεσίες"
            : "Find Online Documents and Services Easily"}
        </h1>

        {!dynamicTitle && (
          <p
            className="
              text-blue-200 font-medium 
              max-w-3xl leading-snug 
              [font-size:clamp(0.8rem,1.4vw,1rem)]
              text-center
              transition-all duration-500 ease-in-out
            "
          >
            {language === "el"
              ? "Γρήγορη πρόσβαση σε χρήσιμους συνδέσμους για φορολογικά, ασφαλιστικά, υγειονομικά και άλλα έγγραφα και υπηρεσίες."
              : "Quick access to useful links for tax, insurance, health, and other public services."}
          </p>
        )}
      </div>

      {/* === Right Section (Desktop Buttons) === */}
      <div
        className="
          hidden md:flex items-center justify-end gap-4 md:w-full
          transition-all duration-500 ease-in-out
        "
      >
        {!isHome && (
          <button
            onClick={handleBack}
            className="
              flex items-center gap-1 text-sm text-blue-100 
              px-3 py-1.5 rounded-lg border border-white/20 
              bg-white/10 backdrop-blur-md 
              hover:bg-white/20 transition-all duration-500 ease-in-out
            "
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">
              {language === "el" ? "Πίσω" : "Back"}
            </span>
          </button>
        )}

        <button
          onClick={() => setLanguage(language === "el" ? "en" : "el")}
          className="flex items-center gap-1 hover:text-blue-300 transition-all duration-500 ease-in-out text-sm"
        >
          <Globe size={16} />
          {language === "el" ? "EN" : "ΕΛ"}
        </button>
      </div>
    </header>
  );
};

export default Header;
